import {Modal, Table} from "antd";
import {Nullable} from "../../common/utils/nullable";
import "./index.scss";
import React from "react";
import AuthZButton from "src/components/AuthzButton";
import {downloadFile} from "../../common/utils/file-utils";
import {ErrorResponse} from "../../common/errors";
import {convertJSONtoCSV, isValidJSON} from "../../common/utils/functions";
import {ReviewAction, sendReviewEvent} from "../matomoClient";

type Props = {
  isVisible: boolean;
  setVisibility: (isVisible: boolean) => void;
  error: Nullable<ErrorResponse>,
  appName:String
};

enum ErrorCategories {
  "INACTIVE_USERS" = "Inactive users",
  "DUPLICATE_RECORDS" = "Duplicate records",
  "INVALID_RECORDS" = "Invalid records",
  "MISSING_COLUMNS" = "Missing columns",
  "INVALID_CSV" = "Invalid CSV",
  "NEEDS_REMOVAL" = "Needs removal",
}

function ErrorModal(props: Props) {
  function renderTable(err: ErrorResponse[0]) {
    const dataSource = JSON.parse(err.errorMessage);
    const columns = Object.keys(dataSource[0]).map((key) => ({key, title: key, dataIndex: key}))
    return <Table dataSource={dataSource} columns={columns} pagination={false}/>
  }

  const renderErrorAsPlainText = (err, errorNumber: number) => (
    <div key={err.errorKey}>
      <b>{errorNumber}. {ErrorCategories[err.errorKey] || err.errorKey}:</b> {err.errorMessage}
    </div>
  )

  const renderErrorInATable = (err, errorNumber: number) => (
    <div key={err.errorKey}>
      <b>{errorNumber}. {ErrorCategories[err.errorKey]}:</b>
      <br/>
      {renderTable(err)}
    </div>
  );

  const errorRendererMap = (err: ErrorResponse[0], index) => {
    const errorNumber = index + 1;
    const handlerMap = {
      "INACTIVE_USERS": (err) => renderErrorInATable(err, errorNumber),
      "DUPLICATE_RECORDS": (err) => renderErrorInATable(err, errorNumber),
      "INVALID_RECORDS": (err) => renderErrorInATable(err, errorNumber),
      "MISSING_COLUMNS": (err) => renderErrorAsPlainText(err, errorNumber),
      "INVALID_CSV": (err) => renderErrorAsPlainText(err, errorNumber),
      "NEEDS_REMOVAL": (err) => renderErrorInATable(err, errorNumber),
    }
    return handlerMap[err.errorKey]
      ? handlerMap[err.errorKey](err)
      : renderErrorAsPlainText(err, errorNumber)
  }

  function renderErrorMessage() {
    if (props.error) {
      return (
        <>
          {props.error!!.map(errorRendererMap)}
        </>
      );
    }
    return <div>No errors to be displayed</div>
  }

  const onErrorFileDownloadButtonClick = () => {
    sendReviewEvent(ReviewAction.DownloadErrorsOnSubmittingUploadedFiles, props.appName)
    downloadFile(new File(
      [
        props.error!!
          .map((err, index) => {
            const errorTitle = `\n${index + 1}. ,${ErrorCategories[err.errorKey] || err.errorKey}:`
            if (isValidJSON(err.errorMessage)) {
              return errorTitle.concat(
                `,${convertJSONtoCSV(JSON.parse(err.errorMessage), 0, 2)}`)
            }
            return errorTitle.concat(`,${err.errorMessage}\n`)
          })
          .join("")
      ],
      "file-errors.csv",
      {type: "text/csv"}
    ))
  }

  function renderTitle() {
    return (
      <>
        <div>Upload Error Details</div>
        {props.error &&
          // @ts-ignore
          <AuthZButton
            authZButtonType="secondary"
            onClick={onErrorFileDownloadButtonClick}
          >
            Download error file
          </AuthZButton>
        }
      </>
    );
  }

  return (
    <Modal
      className="ErrorModal"
      visible={props.isVisible}
      width={"auto"}
      title={renderTitle()}
      centered={true}
      closable={false}
      footer={[
        // @ts-ignore
        <AuthZButton
          authZButtonType="secondary"
          onClick={() => props.setVisibility(false)}
        >
          Close
        </AuthZButton>
      ]}
    >
      <div className="error-action-msg">
        File submission has failed, mentioned below is the list of errors that needs your attention.<br/>
        Please update the files and try again.
      </div>
      {renderErrorMessage()}
    </Modal>
  );
}

export default ErrorModal;
